// src/services/staffConfirmation/canonicalJson.ts - RFC 8785 (JCS) canonical JSON.
//
// A staff confirmation is bound to a hash of the exact request body, and the api
// re-derives that hash from the body it receives. Both sides must therefore agree
// on one byte-exact serialisation. RFC 8785 fixes it:
//   - object members sorted by their names' UTF-16 code units, no whitespace;
//   - numbers in the ECMAScript shortest round-trip form (what JSON.stringify
//     writes for a finite number), so 4.50 is 4.5 and 1E30 is 1e+30;
//   - strings escaped exactly as JSON.stringify escapes them.
// The office sends THIS string as the body (see bindStaffConfirmation), so the
// bytes hashed and the bytes sent are the same bytes.
//
// Input must be I-JSON (RFC 7493): a non-finite number or a lone surrogate is
// refused rather than silently rewritten, because a body the client cannot state
// exactly must not be confirmed.

export class CanonicalJsonError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CanonicalJsonError";
  }
}

const isHigh = (unit: number) => unit >= 0xd800 && unit <= 0xdbff;
const isLow = (unit: number) => unit >= 0xdc00 && unit <= 0xdfff;

const hasLoneSurrogate = (value: string): boolean => {
  for (let i = 0; i < value.length; i += 1) {
    const unit = value.charCodeAt(i);
    if (isHigh(unit)) {
      if (!isLow(value.charCodeAt(i + 1))) return true;
      i += 1;
    } else if (isLow(unit)) {
      return true;
    }
  }
  return false;
};

const canonicalString = (value: string): string => {
  if (hasLoneSurrogate(value))
    throw new CanonicalJsonError("A string holds a lone surrogate.");
  return JSON.stringify(value);
};

const canonicalNumber = (value: number): string => {
  if (!Number.isFinite(value))
    throw new CanonicalJsonError("A number is not finite.");
  // JSON.stringify writes the ECMAScript Number-to-String form RFC 8785 adopts
  // (and -0 as 0).
  return JSON.stringify(value);
};

/** What JSON.stringify would see after `toJSON` (Date, BigNumber, ...). */
const jsonValueOf = (value: unknown): unknown => {
  if (
    value !== null &&
    typeof value === "object" &&
    typeof (value as { toJSON?: unknown }).toJSON === "function"
  ) {
    return (value as { toJSON: () => unknown }).toJSON();
  }
  return value;
};

/** True for a member JSON.stringify drops from an object. */
const isDropped = (value: unknown): boolean =>
  value === undefined ||
  typeof value === "function" ||
  typeof value === "symbol";

const serialise = (raw: unknown, seen: Set<object>): string => {
  const value = jsonValueOf(raw);
  if (value === null) return "null";
  switch (typeof value) {
    case "boolean":
      return value ? "true" : "false";
    case "number":
      return canonicalNumber(value);
    case "string":
      return canonicalString(value);
    case "object":
      break;
    default:
      throw new CanonicalJsonError(`A ${typeof value} has no JSON form.`);
  }
  const node = value as object;
  if (seen.has(node)) throw new CanonicalJsonError("The value is circular.");
  seen.add(node);
  try {
    if (Array.isArray(node)) {
      // JSON.stringify writes a missing array element as null; so does this.
      return `[${node
        .map((item) =>
          isDropped(jsonValueOf(item)) ? "null" : serialise(item, seen)
        )
        .join(",")}]`;
    }
    const proto = Object.getPrototypeOf(node);
    if (proto !== Object.prototype && proto !== null)
      throw new CanonicalJsonError("Only plain objects can be canonicalised.");
    const record = node as Record<string, unknown>;
    // Default sort compares UTF-16 code units, which is RFC 8785's order.
    const members = Object.keys(record)
      .filter((key) => !isDropped(jsonValueOf(record[key])))
      .sort()
      .map((key) => `${canonicalString(key)}:${serialise(record[key], seen)}`);
    return `{${members.join(",")}}`;
  } finally {
    seen.delete(node);
  }
};

/** The RFC 8785 canonical form of a JSON value. Throws CanonicalJsonError. */
export const canonicalJson = (value: unknown): string =>
  serialise(value, new Set<object>());
